import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { Helmet } from "react-helmet";
import * as apiClient from "../api-client";
import Loader from "../components/LoaderComponent";
import { HelmetProvider } from "react-helmet-async";
import BlogSliderHome from "../components/Blogs/BlogSliderHome";
import { useEffect } from "react";

const BlogDetail = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { slug } = useParams<{ slug: string }>();

  const { data: blog, error, isLoading } = useQuery(
    ["fetchBlogBySlug", slug],
    () => apiClient.fetchBlogBySlug(slug!),
    { enabled: !!slug }
  );

  if (isLoading) return <Loader />;
  if (error) return <div>Error loading blog</div>;

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>{blog?.metaTitle || blog?.title}</title>
          <meta name="description" content={blog?.metaDescription || blog?.content.slice(0, 150)} />
          <link rel="canonical" href={`https://www.haloholidays.co.uk/blogs/${slug}`} />
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "http://schema.org",
              "@type": "BlogPosting",
              "headline": blog?.title,
              "image": blog?.imageUrls[0],
              "author": {
                "@type": "Person",
                "name": blog?.authorName
              },
              "datePublished": blog?.createdAt,
              "articleBody": blog?.content,
            })}
          </script>
        </Helmet>
      </HelmetProvider>
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <div className="text-halo-pink text-lg font-medium mb-2 capitalize">
              {blog?.type}
            </div>
            <h1 className="text-3xl font-semibold">{blog?.title}</h1>
            <div className="text-gray-500 text-sm mt-2">
              {new Date(blog!.createdAt).toLocaleDateString()}
            </div>
          </div>
          <div className="mb-10">
            {blog!.imageUrls && blog!.imageUrls.length > 0 && (
              <img
                src={blog!.imageUrls[0]}
                alt={blog!.title}
                className="w-full rounded-lg"
              />
            )}
          </div>
          <div className="max-w-4xl mx-auto">
            <p className="text-base mb-4 text-gray-800" dangerouslySetInnerHTML={{ __html: blog?.content || '' }}></p>
            <div className="quote mt-8 mb-8">
              <div className="quote__line bg-blue-1 rounded-lg" />
              <div className="quote__icon">
                <img src="/quote-light.svg" alt="icon" />
              </div>
              <div className="text-[18px] font-medium leading-10" dangerouslySetInnerHTML={{ __html: blog?.quote || '' }}></div>
            </div>
            {blog?.sections.map((section, index) => (
              <div key={index} className="mb-10">
                <h2 className="text-xl font-semibold mt-8" dangerouslySetInnerHTML={{ __html: section.heading || '' }}></h2>
                <p className="text-base mt-4 text-gray-800" dangerouslySetInnerHTML={{ __html: section.text || '' }}></p>
                {section.image && (
                  <div className="my-8">
                    <img src={section.image} alt={section.heading} className="rounded-lg w-full" />
                  </div>
                )}
                {section.listItems.map((listItem, listIndex) => (
                  <div key={listIndex} className="mt-4">
                    <h3 className="text-lg font-medium text-black mb-4" dangerouslySetInnerHTML={{ __html: listItem.header }}></h3>
                    <ul className="custom-bullet pl-5 space-y-1 text-gray-500">
                      {listItem.paragraph.split('\n').map((item, index) => (
                        item.length > 0 ?
                          <li key={index} dangerouslySetInnerHTML={{ __html: item }}></li> : ''
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="border-t border-gray-200 pt-10 "></div>
      <section className="pb-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-semibold">Related content</h2>
          </div>
          <div className="px-4">
            <BlogSliderHome />
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogDetail;
